import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import PageHeader from '../../components/ui/PageHeader';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const Profile = () => {
  const { user, userProfile, updateProfile, loading } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    full_name: userProfile?.full_name || '',
    job_title: userProfile?.job_title || '',
    department: userProfile?.department || '',
    bio: userProfile?.bio || '',
    timezone: userProfile?.timezone || 'UTC',
  });

  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const getInitials = () => {
    const name = userProfile?.full_name || user?.email || 'U';
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      const { error: updateError } = await updateProfile(formData);
      if (updateError) {
        setError(updateError?.message || 'Failed to update profile');
      } else {
        setSaveSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    setPasswordError('');
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (passwordData?.newPassword?.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    setIsChangingPassword(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: passwordData?.newPassword });
      if (error) {
        setPasswordError(error?.message);
      } else {
        setPasswordSuccess(true);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setPasswordSuccess(false), 3000);
      }
    } catch (err) {
      setPasswordError('Failed to update password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const roleColors = {
    admin: 'bg-error-100 text-error',
    manager: 'bg-warning-100 text-warning',
    member: 'bg-primary-100 text-primary',
    viewer: 'bg-secondary-100 text-secondary-600',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />
        <main className="lg:ml-60 pt-16">
          <div className="p-6 flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />
      <main className="lg:ml-60 pt-16">
        <div className="p-6">
          <PageHeader
            title="Profile"
            description="Manage your personal information and account settings"
            actions={
              <>
                {isEditing ? (
                  <>
                    <button onClick={() => setIsEditing(false)}
                      className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors duration-200">
                      Cancel
                    </button>
                    <button onClick={handleSave} disabled={isSaving}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50">
                      {isSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={16} />}
                      <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                  </>
                ) : (
                  <button onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2">
                    <Icon name="Edit" size={16} />
                    <span>Edit Profile</span>
                  </button>
                )}
              </>
            }
          />

          {saveSuccess && (
            <div className="mb-6 p-4 bg-success-50 border border-success-100 rounded-lg flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <p className="text-sm text-success">Profile updated successfully!</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-100 rounded-lg flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Card */}
            <div className="lg:col-span-1">
              <div className="bg-surface rounded-xl border border-border p-6">
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-3xl font-bold text-primary-700">{getInitials()}</span>
                  </div>
                  <h2 className="text-xl font-semibold text-text-primary">{userProfile?.full_name || 'User'}</h2>
                  <p className="text-text-secondary text-sm mt-1">{userProfile?.job_title || 'Team Member'}</p>
                  <span className={`mt-2 px-3 py-1 rounded-full text-xs font-medium capitalize ${roleColors?.[userProfile?.role] || 'bg-secondary-100 text-secondary-600'}`}>
                    {userProfile?.role || 'member'}
                  </span>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center space-x-3 text-sm">
                    <Icon name="Mail" size={16} color="var(--color-secondary-400)" />
                    <span className="text-text-secondary truncate">{user?.email}</span>
                  </div>
                  {userProfile?.department && (
                    <div className="flex items-center space-x-3 text-sm">
                      <Icon name="Building" size={16} color="var(--color-secondary-400)" />
                      <span className="text-text-secondary">{userProfile?.department}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-3 text-sm">
                    <Icon name="Globe" size={16} color="var(--color-secondary-400)" />
                    <span className="text-text-secondary">{userProfile?.timezone || 'UTC'}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <Icon name="Calendar" size={16} color="var(--color-secondary-400)" />
                    <span className="text-text-secondary">Joined {userProfile?.created_at ? new Date(userProfile.created_at)?.toLocaleDateString() : 'Recently'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Edit Form */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-surface rounded-xl border border-border p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-6">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Full Name</label>
                    {isEditing ? (
                      <input type="text" name="full_name" value={formData?.full_name} onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200" />
                    ) : (
                      <p className="px-4 py-3 bg-secondary-50 rounded-lg text-text-primary">{userProfile?.full_name || '—'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Job Title</label>
                    {isEditing ? (
                      <input type="text" name="job_title" value={formData?.job_title} onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                        placeholder="e.g. Frontend Developer" />
                    ) : (
                      <p className="px-4 py-3 bg-secondary-50 rounded-lg text-text-primary">{userProfile?.job_title || '—'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Department</label>
                    {isEditing ? (
                      <input type="text" name="department" value={formData?.department} onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                        placeholder="e.g. Engineering" />
                    ) : (
                      <p className="px-4 py-3 bg-secondary-50 rounded-lg text-text-primary">{userProfile?.department || '—'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Timezone</label>
                    {isEditing ? (
                      <select name="timezone" value={formData?.timezone} onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200">
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="Europe/London">London (GMT)</option>
                        <option value="Europe/Paris">Paris (CET)</option>
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                        <option value="Asia/Kolkata">India (IST)</option>
                      </select>
                    ) : (
                      <p className="px-4 py-3 bg-secondary-50 rounded-lg text-text-primary">{userProfile?.timezone || 'UTC'}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text-primary mb-2">Bio</label>
                    {isEditing ? (
                      <textarea name="bio" value={formData?.bio} onChange={handleInputChange} rows={3}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 resize-none"
                        placeholder="Tell your team about yourself..." />
                    ) : (
                      <p className="px-4 py-3 bg-secondary-50 rounded-lg text-text-primary min-h-[80px]">{userProfile?.bio || '—'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Change Password */}
              <div className="bg-surface rounded-xl border border-border p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-6">Change Password</h3>
                {passwordSuccess && (
                  <div className="mb-4 p-3 bg-success-50 border border-success-100 rounded-lg flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                    <p className="text-sm text-success">Password updated successfully!</p>
                  </div>
                )}
                {passwordError && (
                  <div className="mb-4 p-3 bg-error-50 border border-error-100 rounded-lg flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                    <p className="text-sm text-error">{passwordError}</p>
                  </div>
                )}
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">New Password</label>
                    <input type="password" value={passwordData?.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e?.target?.value }))}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="Enter new password" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Confirm New Password</label>
                    <input type="password" value={passwordData?.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="Confirm new password" />
                  </div>
                  <button type="submit" disabled={isChangingPassword}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50">
                    {isChangingPassword ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Lock" size={16} />}
                    <span>{isChangingPassword ? 'Updating...' : 'Update Password'}</span>
                  </button>
                </form>
              </div>

              {/* Account Info */}
              <div className="bg-surface rounded-xl border border-border p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Account Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <div>
                      <p className="text-sm font-medium text-text-primary">Email Address</p>
                      <p className="text-sm text-text-secondary">{user?.email}</p>
                    </div>
                    <span className="px-2 py-1 bg-success-50 text-success text-xs rounded-full">Verified</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <div>
                      <p className="text-sm font-medium text-text-primary">Account Role</p>
                      <p className="text-sm text-text-secondary capitalize">{userProfile?.role || 'member'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-text-primary">Member Since</p>
                      <p className="text-sm text-text-secondary">{userProfile?.created_at ? new Date(userProfile.created_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Recently'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
